import { useMemo, useState } from 'react'
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts'
import { money, compactMoney, dateLabel, monthLabel } from '../lib/format.js'

const axis = { stroke: 'var(--text-secondary)', fontSize: 11, tickLine: false, axisLine: false }

function TooltipBox({ active, payload, label, labelFormat }) {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tooltip">
      <div className="fw-semibold mb-1">{labelFormat(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="row-line">
          <span><span className="swatch" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: p.color, marginRight: 6 }} />{p.name}</span>
          <span>{money(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

export function Legend({ items }) {
  return (
    <div className="legend">
      {items.map((i) => <span key={i.label}><span className="swatch" style={{ background: i.color }} />{i.label}</span>)}
    </div>
  )
}

const RANGES = [
  { id: '3m', label: '3M', days: 92 },
  { id: '1y', label: '1Y', days: 366 },
  { id: 'all', label: 'All' },
]

export function RangePicker({ value, onChange }) {
  return (
    <div className="btn-group btn-group-sm">
      {RANGES.map((r) => (
        <button key={r.id} className={`btn ${value === r.id ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => onChange(r.id)}>{r.label}</button>
      ))}
    </div>
  )
}

export function useRange(series, initial = '1y') {
  const [range, setRange] = useState(initial)
  const filtered = useMemo(() => {
    const r = RANGES.find((x) => x.id === range)
    if (!r.days || !series.length) return series
    const cutoff = new Date(Date.now() - r.days * 86400000).toISOString().slice(0, 10)
    return series.filter((s) => s.date >= cutoff)
  }, [series, range])
  return { range, setRange, filtered }
}

const NW_SERIES = [
  { key: 'net', label: 'Net worth', color: 'var(--series-1)' },
  { key: 'assets', label: 'Assets', color: 'var(--series-3)' },
  { key: 'debt', label: 'Debt', color: 'var(--series-2)' },
]

export function NetWorthChart({ data, height = 280 }) {
  if (!data.length) return <div className="empty">No snapshots yet. One is saved automatically each day you open the app.</div>
  return (
    <>
      <Legend items={NW_SERIES} />
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--grid)" vertical={false} />
          <XAxis dataKey="date" {...axis} tickFormatter={(d) => dateLabel(d).replace(/ \d{4}$/, '')} minTickGap={40} />
          <YAxis {...axis} tickFormatter={compactMoney} width={64} />
          <ReferenceLine y={0} stroke="var(--text-secondary)" strokeOpacity={0.4} />
          <Tooltip content={<TooltipBox labelFormat={dateLabel} />} cursor={{ stroke: 'var(--text-secondary)', strokeDasharray: '3 3' }} />
          {NW_SERIES.map((s) => (
            <Line key={s.key} type="stepAfter" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }} isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  )
}

const IO_SERIES = [
  { key: 'income', label: 'Income', color: 'var(--series-1)' },
  { key: 'expense', label: 'Outcome', color: 'var(--series-2)' },
]

export function IncomeExpenseChart({ data, height = 260 }) {
  if (!data.length) return <div className="empty">No transactions yet.</div>
  return (
    <>
      <Legend items={IO_SERIES} />
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap="25%">
          <CartesianGrid stroke="var(--grid)" vertical={false} />
          <XAxis dataKey="month" {...axis} tickFormatter={(m) => monthLabel(m, 'short')} />
          <YAxis {...axis} tickFormatter={compactMoney} width={64} />
          <Tooltip content={<TooltipBox labelFormat={(m) => monthLabel(m)} />} cursor={{ fill: 'var(--bs-tertiary-bg)' }} />
          {IO_SERIES.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </>
  )
}

/** Magnitude comparison as a ranked bar list (clearer than a pie). */
export function BarList({ items, total }) {
  if (!items.length) return <div className="empty">Nothing here yet.</div>
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1)
  return (
    <div className="bar-list">
      {items.map((i) => (
        <div key={i.label} className="bar-row" title={`${i.label}: ${money(i.value)}`}>
          <span className="text-truncate">{i.label}</span>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${(Math.abs(i.value) / max) * 100}%` }} /></div>
          <span className="num">{money(i.value)}{total ? <span className="text-body-secondary small ms-2">{Math.round((i.value / total) * 100)}%</span> : null}</span>
        </div>
      ))}
    </div>
  )
}
